import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportToExcel } from "@/lib/exports";
import {
  Wallet, ArrowRight, FileCheck2, FileSpreadsheet, X, CheckCircle2, Circle, Layers,
} from "lucide-react";
import { toast } from "sonner";

function docsBadgeClasses(uploaded, total) {
  const u = Number(uploaded) || 0;
  const t = Number(total) || 7;
  if (u >= t) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (u >= Math.ceil(t * 0.75)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (u >= Math.ceil(t * 0.4))  return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-red-100 text-red-800 border-red-200";
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthStartIso = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};

export default function FinanceList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canEditDisb = user?.role === "admin" || user?.role === "finance";

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters (applied)
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  const [disbFilter, setDisbFilter] = useState(params.get("disbursement") || "");
  const [doFilter, setDoFilter] = useState(params.get("delivery_order") || "");
  // Draft (before Apply)
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const [draftDisb, setDraftDisb] = useState(disbFilter);
  const [draftDo, setDraftDo] = useState(doFilter);

  const [editing, setEditing] = useState(null); // loan-disbursement modal target
  const [editingDO, setEditingDO] = useState(null); // delivery-order confirm target
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulk, setShowBulk] = useState(false);
  const [showBulkDo, setShowBulkDo] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/finance", { params: { full: true } })
      .then(({ data }) => setVehicles(data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Sync active filters back to the URL so /finance?disbursement=Yes shareable & Dashboard-driven.
  useEffect(() => {
    const next = new URLSearchParams();
    if (dateFrom) next.set("date_from", dateFrom);
    if (dateTo) next.set("date_to", dateTo);
    if (disbFilter) next.set("disbursement", disbFilter);
    if (doFilter) next.set("delivery_order", doFilter);
    setParams(next, { replace: true });
  }, [dateFrom, dateTo, disbFilter, doFilter, setParams]);

  const applyFilters = () => {
    setDateFrom(draftFrom); setDateTo(draftTo);
    setDisbFilter(draftDisb); setDoFilter(draftDo);
  };
  const resetFilters = () => {
    setDraftFrom(""); setDraftTo(""); setDraftDisb(""); setDraftDo("");
    setDateFrom(""); setDateTo(""); setDisbFilter(""); setDoFilter("");
  };
  const thisMonth = () => {
    setDraftFrom(monthStartIso()); setDraftTo(todayIso());
  };

  // Sort state (used mainly by the Age column on the Pending view).
  const [sortKey, setSortKey] = useState(null);   // 'days_pending' | null
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  // Attach a `days_pending` field on the fly + boolean overdue helper.
  const daysPending = (dod) => {
    if (!dod) return null;
    const d = new Date(String(dod).slice(0, 10));
    if (Number.isNaN(d.getTime())) return null;
    const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
    return diff < 0 ? 0 : diff;
  };
  const isOverduePending = (v) => {
    if (!v.has_finance) return false;
    if ((v.payment_type || "Loan") !== "Loan") return false;
    if ((v.loan_disbursement || "No") === "Yes") return false;
    const dp = daysPending(v.date_of_delivery);
    return dp != null && dp > 7;
  };
  const isOverdueDoPending = (v) => {
    if (!v.has_finance) return false;
    if ((v.payment_type || "Loan") !== "Loan") return false;
    if ((v.delivery_order || "No") === "Yes") return false;
    const dp = daysPending(v.date_of_delivery);
    return dp != null && dp > 7;
  };

  const showPendingView = disbFilter === "Pending" || doFilter === "No";

  const filtered = useMemo(() => {
    let out = vehicles.filter(v => {
      const dod = (v.date_of_delivery || "").slice(0, 10);
      if (dateFrom && (!dod || dod < dateFrom)) return false;
      if (dateTo && (!dod || dod > dateTo)) return false;

      // Disbursement filter — Loan-only for Yes & Pending (matches Dashboard cards).
      if (disbFilter === "Pending") {
        if (!v.has_finance) return false;
        if ((v.payment_type || "Loan") !== "Loan") return false;
        if ((v.loan_disbursement || "No") === "Yes") return false;
      } else if (disbFilter === "Yes") {
        if (!v.has_finance) return false;
        if ((v.payment_type || "Loan") !== "Loan") return false;
        if ((v.loan_disbursement || "No") !== "Yes") return false;
      } else if (disbFilter === "No") {
        if ((v.loan_disbursement || "No") !== "No") return false;
      }

      // Delivery Order filter — always Loan-only (per spec cash sales are excluded).
      if (doFilter === "Yes" || doFilter === "No") {
        if (!v.has_finance) return false;
        if ((v.payment_type || "Loan") !== "Loan") return false;
        if ((v.delivery_order || "No") !== doFilter) return false;
      }
      return true;
    }).map(v => ({ ...v, days_pending: daysPending(v.date_of_delivery) }));
    if (sortKey === "days_pending") {
      out.sort((a, b) => {
        const av = a.days_pending == null ? -Infinity : a.days_pending;
        const bv = b.days_pending == null ? -Infinity : b.days_pending;
        return sortDir === "asc" ? av - bv : bv - av;
      });
    }
    return out;
  }, [vehicles, dateFrom, dateTo, disbFilter, doFilter, sortKey, sortDir]);

  const totals = useMemo(() => {
    let disbCount = 0, disbAmt = 0;
    for (const r of filtered) {
      if ((r.loan_disbursement || "No") === "Yes") {
        disbCount++;
        disbAmt += Number(r.loan_amount || 0);
      }
    }
    return { count: filtered.length, disbCount, disbAmt };
  }, [filtered]);

  const handleExport = () => {
    if (filtered.length === 0) { toast.info("No rows to export"); return; }
    const columns = [
      { key: "_sr", label: "SR. No." },
      { key: "customer_name", label: "Customer Name" },
      { key: "customer_phone", label: "Mobile Number" },
      { key: "model", label: "Model" },
      { key: "chassis_number", label: "Chassis" },
      { key: "engine_number", label: "Engine" },
      { key: "branch", label: "Branch" },
      { key: "color", label: "Color" },
      { key: "date_of_delivery", label: "Date of Delivery" },
      ...(showPendingView ? [{ key: "days_pending", label: "Days Pending", exportFmt: (v) => v == null ? "" : v }] : []),
      { key: "payment_type", label: "Payment Type" },
      { key: "financer_name", label: "Financer" },
      { key: "ex_showroom_price", label: "Ex-Showroom", exportFmt: (v) => INR(v || 0) },
      { key: "loan_amount", label: "Loan Amount", exportFmt: (v) => INR(v || 0) },
      { key: "total_paid", label: "Paid", exportFmt: (v) => INR(v || 0) },
      { key: "remaining_balance", label: "Outstanding", exportFmt: (v) => INR(v || 0) },
      { key: "loan_disbursement", label: "Loan Disbursement", exportFmt: (v) => v || "No" },
      { key: "disbursement_date", label: "Disbursement Date", exportFmt: (v) => v || "" },
      { key: "delivery_order", label: "Delivery Order",
        exportFmt: (v, r) => (r.payment_type || "Loan") === "Loan" ? (v || "No") : "N/A" },
      { key: "docs_uploaded", label: "Docs Uploaded",
        exportFmt: (v, r) => `${v || 0} / ${r.docs_total || 7}` },
    ];
    const rows = filtered.map((r, idx) => ({ ...r, _sr: idx + 1 }));
    exportToExcel(showPendingView ? "finance_pending_disbursement" : "finance_list", rows, columns);
    toast.success(`Exported ${rows.length} rows`);
  };

  const saveDisbursement = async (vehicleId, loan_disbursement, disbursement_date) => {
    try {
      const body = { loan_disbursement };
      if (loan_disbursement === "Yes") {
        body.disbursement_date = disbursement_date || todayIso();
      }
      const { data } = await api.patch(`/finance/${vehicleId}/disbursement`, body);
      setVehicles(prev => prev.map(v => v.id === vehicleId
        ? { ...v, loan_disbursement: data.loan_disbursement, disbursement_date: data.disbursement_date }
        : v));
      toast.success("Loan disbursement updated");
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    }
  };

  const saveDeliveryOrder = async (vehicleId, next) => {
    try {
      const { data } = await api.patch(`/finance/${vehicleId}/delivery-order`, {
        delivery_order: next,
      });
      setVehicles(prev => prev.map(v => v.id === vehicleId
        ? { ...v, delivery_order: data.delivery_order }
        : v));
      toast.success(`Delivery Order set to ${data.delivery_order}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update Delivery Order");
    }
  };

  const toggleAllVisible = () => {
    const allIds = filtered.map(v => v.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    const next = new Set(selectedIds);
    if (allSelected) allIds.forEach(id => next.delete(id));
    else allIds.forEach(id => next.add(id));
    setSelectedIds(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const runBulk = async (loan_disbursement, disbursement_date) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/finance/bulk-disbursement", {
        vehicle_ids: ids,
        loan_disbursement,
        disbursement_date: loan_disbursement === "Yes" ? (disbursement_date || todayIso()) : null,
      });
      const updMap = new Map(data.updated.map(u => [u.vehicle_id, u]));
      setVehicles(prev => prev.map(v => updMap.has(v.id)
        ? { ...v,
            loan_disbursement: updMap.get(v.id).loan_disbursement,
            disbursement_date: updMap.get(v.id).disbursement_date }
        : v));
      setSelectedIds(new Set());
      setShowBulk(false);
      toast.success(`Bulk update: ${data.updated_count}/${data.total} updated${data.failed_count ? `, ${data.failed_count} failed` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk update failed");
    }
  };

  const runBulkDo = async (delivery_order) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/finance/bulk-delivery-order", {
        vehicle_ids: ids, delivery_order,
      });
      const updMap = new Map(data.updated.map(u => [u.vehicle_id, u]));
      setVehicles(prev => prev.map(v => updMap.has(v.id)
        ? { ...v, delivery_order: updMap.get(v.id).delivery_order }
        : v));
      setSelectedIds(new Set());
      setShowBulkDo(false);
      toast.success(`Bulk DO: ${data.updated_count}/${data.total} updated${data.failed_count ? `, ${data.failed_count} failed` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk DO update failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="finance-heading">Finance</h1>
          <p className="text-sm text-slate-500 mt-1">
            Fill finance details, track loan disbursement and generate invoices for delivered vehicles
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEditDisb && selectedIds.size > 0 && (
            <>
              <button data-testid="finance-bulk-disbursement"
                onClick={() => setShowBulk(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                <Layers className="w-4 h-4" /> Bulk Disbursement ({selectedIds.size})
              </button>
              <button data-testid="finance-bulk-delivery-order"
                onClick={() => setShowBulkDo(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-teal-600 hover:bg-teal-700 text-white">
                <Layers className="w-4 h-4" /> Bulk DO ({selectedIds.size})
              </button>
            </>
          )}
          <button data-testid="finance-export-excel"
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border border-slate-300 hover:bg-slate-50 text-slate-700">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Export Excel
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4" data-testid="finance-filter-bar">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <div className="uppercase tracking-wider text-slate-500 mb-1">From (Delivery)</div>
            <input type="date" value={draftFrom} max={draftTo || todayIso()}
              onChange={(e) => setDraftFrom(e.target.value)}
              data-testid="finance-filter-from"
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm" />
          </label>
          <label className="text-xs">
            <div className="uppercase tracking-wider text-slate-500 mb-1">To (Delivery)</div>
            <input type="date" value={draftTo} min={draftFrom} max={todayIso()}
              onChange={(e) => setDraftTo(e.target.value)}
              data-testid="finance-filter-to"
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm" />
          </label>
          <label className="text-xs">
            <div className="uppercase tracking-wider text-slate-500 mb-1">Loan Disbursement</div>
            <select value={draftDisb} onChange={(e) => setDraftDisb(e.target.value)}
              data-testid="finance-filter-disbursement"
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm bg-white">
              <option value="">All</option>
              <option value="Yes">Disbursed (Yes)</option>
              <option value="No">Not Disbursed (No)</option>
              <option value="Pending">Pending — Loan only</option>
            </select>
          </label>
          <label className="text-xs">
            <div className="uppercase tracking-wider text-slate-500 mb-1">Delivery Order</div>
            <select value={draftDo} onChange={(e) => setDraftDo(e.target.value)}
              data-testid="finance-filter-delivery-order"
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm bg-white">
              <option value="">All</option>
              <option value="Yes">DO Issued (Loan)</option>
              <option value="No">DO Pending (Loan)</option>
            </select>
          </label>
          <button onClick={applyFilters} data-testid="finance-filter-apply"
            className="px-4 py-1.5 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white">
            Apply
          </button>
          <button onClick={thisMonth} data-testid="finance-filter-this-month"
            className="px-3 py-1.5 rounded-md text-sm border border-slate-300 hover:bg-slate-50 text-slate-700">
            This Month
          </button>
          <button onClick={resetFilters} data-testid="finance-filter-reset"
            className="px-3 py-1.5 rounded-md text-sm border border-slate-300 hover:bg-slate-50 text-slate-700">
            Reset
          </button>
          <div className="ml-auto flex gap-4 text-xs">
            <div>
              <div className="uppercase tracking-wider text-slate-500">Rows</div>
              <div className="font-heading text-lg font-bold text-slate-900" data-testid="finance-count-rows">
                {totals.count}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wider text-slate-500">Disbursed</div>
              <div className="font-heading text-lg font-bold text-emerald-700" data-testid="finance-count-disbursed">
                {totals.disbCount}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wider text-slate-500">Disbursed Amt</div>
              <div className="font-heading text-lg font-bold text-emerald-700" data-testid="finance-amount-disbursed">
                {INR(totals.disbAmt)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active filters pill row — makes it clear WHY records are filtered when arriving from Dashboard */}
      {(disbFilter || doFilter || dateFrom || dateTo) && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="finance-active-filters">
          <span className="text-slate-500 uppercase tracking-wider">Active filters:</span>
          {disbFilter === "Yes" && (
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 border border-green-200"
                  data-testid="active-filter-disbursement-yes">
              Delivered + Loan + Disbursement = Yes
            </span>
          )}
          {disbFilter === "Pending" && (
            <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-800 border border-orange-200"
                  data-testid="active-filter-disbursement-pending">
              Delivered + Loan + Disbursement = No
            </span>
          )}
          {disbFilter === "No" && (
            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
              Loan Disbursement = No
            </span>
          )}
          {doFilter === "Yes" && (
            <span className="px-2 py-1 rounded-full bg-teal-100 text-teal-800 border border-teal-200"
                  data-testid="active-filter-do-yes">
              Delivered + Loan + Delivery Order = Yes
            </span>
          )}
          {doFilter === "No" && (
            <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200"
                  data-testid="active-filter-do-no">
              Delivered + Loan + Delivery Order = No
            </span>
          )}
          {(dateFrom || dateTo) && (
            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
              Delivery {dateFrom || "…"} → {dateTo || "…"}
            </span>
          )}
          <button onClick={resetFilters}
            data-testid="active-filters-clear"
            className="ml-1 text-slate-500 hover:text-slate-800 underline underline-offset-2">
            Clear all
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full">
          <thead>
            <tr>
              {canEditDisb && (
                <th className="w-8">
                  <input type="checkbox"
                    data-testid="finance-select-all"
                    checked={filtered.length > 0 && filtered.every(v => selectedIds.has(v.id))}
                    onChange={toggleAllVisible} />
                </th>
              )}
              <th className="col-sr">SR. No.</th>
              <th>Customer Name</th>
              <th>Mobile Number</th>
              <th>Model</th>
              <th>Chassis</th>
              <th>Branch</th>
              <th>Financer</th>
              <th className="text-right">Loan Amount</th>
              <th>Delivery Date</th>
              {showPendingView && (
                <th data-testid="finance-days-pending-th"
                    onClick={() => toggleSort("days_pending")}
                    className="cursor-pointer select-none hover:bg-slate-100">
                  Days Pending
                  {sortKey === "days_pending" && (
                    <span className="ml-1 text-slate-400">{sortDir === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
              )}
              <th>Loan Disbursement</th>
              <th>Delivery Order</th>
              <th>Docs</th>
              <th className="text-right pr-6">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={(canEditDisb ? 14 : 13) + (showPendingView ? 1 : 0)} className="text-center py-10 text-slate-400 text-sm">Loading finance list…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={(canEditDisb ? 14 : 13) + (showPendingView ? 1 : 0)} className="text-center py-10 text-slate-400 text-sm">
                {vehicles.length === 0
                  ? "No delivered vehicles yet. Deliver a vehicle from Vehicle Stock to enable finance."
                  : "No rows match the current filters."}
              </td></tr>
            )}
            {!loading && filtered.map((v, idx) => {
              const uploaded = Number(v.docs_uploaded || 0);
              const total = Number(v.docs_total || 7);
              const disb = v.loan_disbursement || "No";
              const isYes = disb === "Yes";
              const checked = selectedIds.has(v.id);
              const overdue = isOverduePending(v) || isOverdueDoPending(v);
              const dp = v.days_pending;
              return (
                <tr key={v.id} data-testid={`finance-list-row-${v.id}`}
                    data-overdue={overdue ? "1" : "0"}
                    className={overdue ? "bg-red-50 hover:bg-red-100" : ""}>
                  {canEditDisb && (
                    <td className="w-8">
                      <input type="checkbox"
                        data-testid={`finance-row-select-${v.id}`}
                        checked={checked}
                        onChange={() => toggleOne(v.id)} />
                    </td>
                  )}
                  <td className="col-sr">{idx + 1}</td>
                  <td className="text-sm">{v.customer_name || "—"}</td>
                  <td className="text-sm text-slate-500">{v.customer_phone || "—"}</td>
                  <td className="font-medium text-slate-900">{v.model}</td>
                  <td className="font-mono text-xs">{v.chassis_number}</td>
                  <td>{v.branch}</td>
                  <td className="text-sm text-slate-700">{v.financer_name || "—"}</td>
                  <td className="text-right text-sm font-mono">{v.loan_amount ? INR(v.loan_amount) : "—"}</td>
                  <td className="text-xs text-slate-600">{v.date_of_delivery || "—"}</td>
                  {showPendingView && (
                    <td data-testid={`days-pending-${v.id}`}>
                      {dp == null ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                          dp > 7
                            ? "bg-red-100 text-red-800 border-red-200"
                            : dp > 3
                              ? "bg-amber-100 text-amber-800 border-amber-200"
                              : "bg-slate-100 text-slate-700 border-slate-200"
                        }`}>
                          {dp}d
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    <button
                      data-testid={`disbursement-badge-${v.id}`}
                      onClick={() => canEditDisb && setEditing(v)}
                      disabled={!canEditDisb}
                      title={canEditDisb ? "Click to update loan disbursement" : "Read-only"}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
                        isYes
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-200"
                      } ${canEditDisb ? "hover:opacity-80 cursor-pointer" : "cursor-not-allowed opacity-80"}`}>
                      {isYes ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                      {isYes ? `Yes · ${v.disbursement_date || ""}` : "No"}
                    </button>
                  </td>
                  <td>
                    {(() => {
                      const doVal = v.delivery_order || "No";
                      const doYes = doVal === "Yes";
                      const cashRow = (v.payment_type || "Loan") !== "Loan";
                      const canEditDO = canEditDisb && !cashRow;
                      return (
                        <button
                          data-testid={`delivery-order-badge-${v.id}`}
                          onClick={() => canEditDO && setEditingDO(v)}
                          disabled={!canEditDO}
                          title={cashRow
                            ? "Delivery Order applies to Loan sales only"
                            : (canEditDisb ? "Click to toggle Delivery Order" : "Read-only")}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
                            cashRow
                              ? "bg-slate-50 text-slate-400 border-slate-200"
                              : doYes
                                ? "bg-teal-100 text-teal-800 border-teal-200"
                                : "bg-indigo-100 text-indigo-800 border-indigo-200"
                          } ${canEditDO ? "hover:opacity-80 cursor-pointer" : "cursor-not-allowed opacity-80"}`}>
                          {doYes ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                          {cashRow ? "N/A" : doVal}
                        </button>
                      );
                    })()}
                  </td>
                  <td>
                    <span
                      data-testid={`docs-badge-${v.id}`}
                      title={`${uploaded} of ${total} KYC documents uploaded`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${docsBadgeClasses(uploaded, total)}`}>
                      <FileCheck2 className="w-3 h-3" /> {uploaded}/{total}
                    </span>
                  </td>
                  <td className="text-right pr-6">
                    <button data-testid={`open-finance-${v.id}`}
                      onClick={() => navigate(`/finance/${v.id}`)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-xs">
                      <Wallet className="w-3 h-3" /> Open <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <DisbursementModal
          row={editing}
          onClose={() => setEditing(null)}
          onSave={saveDisbursement}
        />
      )}

      {editingDO && (
        <DeliveryOrderModal
          row={editingDO}
          onClose={() => setEditingDO(null)}
          onSave={async (id, next) => { await saveDeliveryOrder(id, next); setEditingDO(null); }}
        />
      )}

      {showBulk && (
        <BulkDisbursementModal
          count={selectedIds.size}
          onClose={() => setShowBulk(false)}
          onSave={runBulk}
        />
      )}

      {showBulkDo && (
        <BulkDeliveryOrderModal
          count={selectedIds.size}
          onClose={() => setShowBulkDo(false)}
          onSave={runBulkDo}
        />
      )}
    </div>
  );
}

function DisbursementModal({ row, onClose, onSave }) {
  const [status, setStatus] = useState(row.loan_disbursement || "No");
  const [date, setDate] = useState(row.disbursement_date || todayIso());
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(row.id, status, status === "Yes" ? date : null); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
         data-testid="disbursement-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-heading text-xl font-semibold text-slate-900">Loan Disbursement</div>
            <div className="text-xs text-slate-500 mt-1">
              {row.model} · <span className="font-mono">{row.chassis_number}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Customer: {row.customer_name || "—"}</div>
          </div>
          <button type="button" onClick={onClose}
            className="p-1 rounded hover:bg-slate-100"
            data-testid="disbursement-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Status</div>
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-sm">
              <button type="button" data-testid="disbursement-set-yes"
                onClick={() => setStatus("Yes")}
                className={`px-4 py-2 ${status === "Yes"
                  ? "bg-emerald-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                Yes — Disbursed
              </button>
              <button type="button" data-testid="disbursement-set-no"
                onClick={() => setStatus("No")}
                className={`px-4 py-2 ${status === "No"
                  ? "bg-slate-700 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                No
              </button>
            </div>
          </div>

          {status === "Yes" && (
            <label className="block text-xs">
              <div className="uppercase tracking-wider text-slate-500 mb-1">Disbursement Date</div>
              <input type="date" value={date} max={todayIso()}
                onChange={(e) => setDate(e.target.value)}
                data-testid="disbursement-date-input"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} data-testid="disbursement-cancel"
            className="px-4 py-2 rounded-md text-sm border border-slate-300">Cancel</button>
          <button type="submit" disabled={saving} data-testid="disbursement-save"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}


function BulkDisbursementModal({ count, onClose, onSave }) {
  const [status, setStatus] = useState("Yes");
  const [date, setDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(status, status === "Yes" ? date : null); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
         data-testid="bulk-disbursement-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-heading text-xl font-semibold text-slate-900">Bulk Loan Disbursement</div>
            <div className="text-xs text-slate-500 mt-1">
              Applying to <b className="text-slate-800">{count}</b> selected file(s)
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="bulk-disbursement-close"
            className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Set Status</div>
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-sm">
              <button type="button" data-testid="bulk-set-yes"
                onClick={() => setStatus("Yes")}
                className={`px-4 py-2 ${status === "Yes" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                Yes — Disbursed
              </button>
              <button type="button" data-testid="bulk-set-no"
                onClick={() => setStatus("No")}
                className={`px-4 py-2 ${status === "No" ? "bg-slate-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                No
              </button>
            </div>
          </div>
          {status === "Yes" && (
            <label className="block text-xs">
              <div className="uppercase tracking-wider text-slate-500 mb-1">Disbursement Date (applied to all)</div>
              <input type="date" value={date} max={todayIso()}
                onChange={(e) => setDate(e.target.value)}
                data-testid="bulk-disbursement-date"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} data-testid="bulk-disbursement-cancel"
            className="px-4 py-2 rounded-md text-sm border border-slate-300">Cancel</button>
          <button type="submit" disabled={saving} data-testid="bulk-disbursement-save"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : `Apply to ${count}`}
          </button>
        </div>
      </form>
    </div>
  );
}


function DeliveryOrderModal({ row, onClose, onSave }) {
  const [next, setNext] = useState(row.delivery_order === "Yes" ? "No" : "Yes");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(row.id, next); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
         data-testid="delivery-order-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-heading text-xl font-semibold text-slate-900">Delivery Order</div>
            <div className="text-xs text-slate-500 mt-1">
              {row.model} · <span className="font-mono">{row.chassis_number}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Customer: {row.customer_name || "—"}</div>
            <div className="text-xs text-slate-500 mt-0.5">Current: <b>{row.delivery_order || "No"}</b></div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="delivery-order-modal-close"
            className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Set To</div>
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-sm">
            <button type="button" data-testid="delivery-order-set-yes"
              onClick={() => setNext("Yes")}
              className={`px-4 py-2 ${next === "Yes" ? "bg-teal-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              Yes — DO Issued
            </button>
            <button type="button" data-testid="delivery-order-set-no"
              onClick={() => setNext("No")}
              className={`px-4 py-2 ${next === "No" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              No
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} data-testid="delivery-order-cancel"
            className="px-4 py-2 rounded-md text-sm border border-slate-300">Cancel</button>
          <button type="submit" disabled={saving} data-testid="delivery-order-save"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}


function BulkDeliveryOrderModal({ count, onClose, onSave }) {
  const [next, setNext] = useState("Yes");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(next); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
         data-testid="bulk-delivery-order-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-heading text-xl font-semibold text-slate-900">Bulk Delivery Order</div>
            <div className="text-xs text-slate-500 mt-1">
              Applying to <b className="text-slate-800">{count}</b> selected file(s).
              Cash rows will still be updated at the API level; the Dashboard cards and Loan-only
              filters continue to ignore Cash sales.
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="bulk-delivery-order-close"
            className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Set Delivery Order</div>
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-sm">
            <button type="button" data-testid="bulk-do-set-yes"
              onClick={() => setNext("Yes")}
              className={`px-4 py-2 ${next === "Yes" ? "bg-teal-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              Yes — DO Issued
            </button>
            <button type="button" data-testid="bulk-do-set-no"
              onClick={() => setNext("No")}
              className={`px-4 py-2 ${next === "No" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              No
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} data-testid="bulk-delivery-order-cancel"
            className="px-4 py-2 rounded-md text-sm border border-slate-300">Cancel</button>
          <button type="submit" disabled={saving} data-testid="bulk-delivery-order-save"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : `Apply to ${count}`}
          </button>
        </div>
      </form>
    </div>
  );
}
